import {
  runCreateResourceFolderWorkflow,
  runDeleteResourceFolderWorkflow,
  runRenameResourceFolderWorkflow,
} from "@/action-dialog";
import { useWorkspaceFlowSessions } from "@/api/flow_sessions";
import { useChatFolders } from "@/api/folders";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { ROUTES } from "@/constants/routes";
import { useActiveChatFlowSessionId } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/chats/useActiveChatFlowSessionId";
import { useChatsRailActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/chats/useChatsRailActions";
import {
  RailRowMenu,
  railRowMenuProps,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowMenu";
import { RailSectionFolderRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSectionFolderRow";
import { RailSectionLoading } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSectionLoading";
import { RailZeroState } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailZeroState";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import type { CanvasFolderActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailCanvasFolderTree";
import {
  ChatFolderDropTarget,
  WorkspacesRailChatFolderTree,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailChatFolderTree";
import { WorkspacesRailChatNode } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailChatNode";
import { route } from "@/utils/route";
import {
  FOLDER_RESOURCE_TYPE_FLOW_SESSION,
  type InboxItem,
} from "@cohesive/api-client";
import { FolderPlus, MessageSquarePlus, MessagesSquare } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

/**
 * The "Chats" sub-section inside an expanded workspace row — the chats scoped to
 * the workspace itself, grouped into chat folders (the same shared folders
 * primitive as canvases, scoped to resource_type flow_session). Mounted only
 * while the workspace row is open, so the queries run lazily.
 */
export function WorkspacesRailChatsSection({
  workspaceId,
  onNewChat,
}: {
  workspaceId: string;
  /**
   * Create a new chat in this workspace. Called with no argument from the
   * section kebab (root), or with a `folderId` from a chat folder's kebab.
   */
  onNewChat?: (folderId?: string) => void;
}) {
  const { data: chats = [], isLoading: chatsLoading } =
    useWorkspaceFlowSessions(workspaceId);
  const { data: folders = [], isLoading: foldersLoading } =
    useChatFolders(workspaceId);
  const activeFlowSessionId = useActiveChatFlowSessionId();
  const { onOpen, onToggleStar, onRename, onDelete } = useChatsRailActions();
  const menu = useRailRowContextMenu();
  const navigate = useNavigate();

  const openNewFolder = useCallback(
    (parentFolderId: string | null) => {
      void runCreateResourceFolderWorkflow({
        workspaceId,
        resourceType: FOLDER_RESOURCE_TYPE_FLOW_SESSION,
        parentFolderId,
      });
    },
    [workspaceId],
  );

  const folderActions = useMemo<CanvasFolderActions>(
    () => ({
      // Open = the folder's view on the workspace detail page (same target as
      // the detail-page folder menus).
      onOpen: (folder) =>
        navigate(
          route(
            ROUTES.WORKSPACES.DETAIL,
            { workspaceId },
            { chatFolder: folder.id },
          ),
        ),
      onNewResource: (folderId) => onNewChat?.(folderId),
      onNewSubfolder: (parentFolderId) => openNewFolder(parentFolderId),
      onRename: (folder) => {
        void runRenameResourceFolderWorkflow({ workspaceId, folder });
      },
      onDelete: (folder) => {
        void runDeleteResourceFolderWorkflow({ workspaceId, folder });
      },
    }),
    [workspaceId, openNewFolder, onNewChat, navigate],
  );

  const renderChatRow = useCallback(
    (chat: InboxItem) => (
      <WorkspacesRailChatNode
        key={chat.id}
        item={chat}
        dragNamespace={`ws-chats:${workspaceId}`}
        active={activeFlowSessionId === chat.flow_session_id}
        onOpen={onOpen}
        onToggleStar={onToggleStar}
        onRename={onRename}
        onDelete={onDelete}
      />
    ),
    [
      workspaceId,
      activeFlowSessionId,
      onOpen,
      onToggleStar,
      onRename,
      onDelete,
    ],
  );

  const isEmpty = chats.length === 0 && folders.length === 0;
  const isLoading = chatsLoading || foldersLoading;

  return (
    /* The whole Chats folder (header + content) is the chats organizing
       surface (chat_folder, folderId=null): drop a chat here to move it to
       the workspace root / un-folder; other kinds show "can't". Works when
       collapsed. Folder headers + chat rows inside win on hover. */
    <ChatFolderDropTarget workspaceId={workspaceId} folderId={null}>
      <RailSectionFolderRow
        label="Chats"
        icon={MessagesSquare}
        expansionKey={`ws:${workspaceId}:chats`}
        onContextMenu={menu.onContextMenu}
        menu={
          <RailRowMenu {...railRowMenuProps(menu)} label="Chats">
            {onNewChat && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  window.setTimeout(onNewChat, 0);
                }}
              >
                <MessageSquarePlus />
                New chat
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                window.setTimeout(() => openNewFolder(null), 0);
              }}
            >
              <FolderPlus />
              New folder
            </DropdownMenuItem>
          </RailRowMenu>
        }
      >
        {isLoading ? (
          <RailSectionLoading label="chats" />
        ) : isEmpty ? (
          <RailZeroState
            icon={MessagesSquare}
            accent="teal"
            variant="passive"
            title="No chats yet."
            description="Workspace chats appear here."
          />
        ) : (
          <WorkspacesRailChatFolderTree
            workspaceId={workspaceId}
            parentFolderId={null}
            folders={folders}
            chats={chats}
            renderChatRow={renderChatRow}
            folderActions={folderActions}
          />
        )}
      </RailSectionFolderRow>
    </ChatFolderDropTarget>
  );
}
